"use client";

import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { GlowCard } from "./Effects";

gsap.registerPlugin(ScrollTrigger);

const MODES = [
  {
    tag: "Standard",
    title: "8D Rotation Engine",
    desc: "Sound orbits around your head with bio-accurate interaural time delay and head-shadow filtering. Circular, sine, triangle, and bounce patterns — free forever.",
    color: "#FF3366",
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10">
        <circle cx="24" cy="24" r="18" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
        <circle cx="24" cy="24" r="10" stroke="currentColor" strokeWidth="1.5" opacity="0.5" />
        <circle cx="24" cy="24" r="3" fill="currentColor" />
        <circle cx="38" cy="18" r="2.5" fill="#FF3366" />
      </svg>
    ),
  },
  {
    tag: "Surround",
    title: "Multi-band Surround",
    desc: "3-band crossover: bass below 250 Hz stays planted, mids orbit with the rotation, highs above 2 kHz counter-shimmer. Free 60-second preview, unlimited with Pro.",
    color: "#FF9F1C",
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10">
        <path d="M10 24c0-8 6-14 14-14" stroke="#FF3366" strokeWidth="2" strokeLinecap="round" />
        <path d="M14 24c0-5.5 4.5-10 10-10" stroke="#FF9F1C" strokeWidth="2" strokeLinecap="round" />
        <path d="M18 24c0-3.3 2.7-6 6-6" stroke="#2EC4B6" strokeWidth="2" strokeLinecap="round" />
        <path d="M38 24c0-8-6-14-14-14" stroke="#FF3366" strokeWidth="2" strokeLinecap="round" />
        <path d="M34 24c0-5.5-4.5-10-10-10" stroke="#FF9F1C" strokeWidth="2" strokeLinecap="round" />
        <path d="M30 24c0-3.3-2.7-6-6-6" stroke="#2EC4B6" strokeWidth="2" strokeLinecap="round" />
        <circle cx="24" cy="24" r="3" fill="currentColor" />
      </svg>
    ),
  },
  {
    tag: "3D",
    title: "HRTF 3D Positioning",
    desc: "The browser's native HRTF engine places sound in true 3D space — Orbit circles you, Figure-8 weaves front to back, Drift wanders with gentle elevation.",
    color: "#2EC4B6",
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10">
        <ellipse cx="24" cy="24" rx="20" ry="8" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
        <ellipse cx="24" cy="24" rx="8" ry="20" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
        <ellipse cx="24" cy="24" rx="14" ry="14" stroke="currentColor" strokeWidth="1" opacity="0.2" />
        <circle cx="24" cy="24" r="3" fill="#2EC4B6" />
        <circle cx="36" cy="20" r="2" fill="#2EC4B6" opacity="0.6" />
      </svg>
    ),
  },
  {
    tag: "Binaural",
    title: "Binaural Beats",
    desc: "Subtle tone layers under your music — Focus 15 Hz, Relax 10 Hz, Meditate 6 Hz, Sleep 3 Hz. They even land in your recordings. Free 60-second preview.",
    color: "#A78BFA",
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10">
        <path d="M6 24c4-6 8 6 12 0s8 6 12 0 8 6 12 0" stroke="#A78BFA" strokeWidth="2" strokeLinecap="round" />
        <path d="M6 28c4-4 8 4 12 0s8 4 12 0 8 4 12 0" stroke="#FF3366" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
        <circle cx="12" cy="14" r="4" stroke="currentColor" strokeWidth="1.5" opacity="0.4" />
        <circle cx="36" cy="14" r="4" stroke="currentColor" strokeWidth="1.5" opacity="0.4" />
      </svg>
    ),
  },
];

export default function Features() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from("[data-feature-card]", {
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 80%",
          toggleActions: "play none none none",
        },
        y: 30,
        autoAlpha: 0,
        duration: 0.5,
        stagger: 0.1,
        ease: "power2.out",
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section id="features" ref={sectionRef} className="section">
      <p className="text-[11px] font-bold uppercase tracking-[2px] text-[#FF9F1C] mb-3">
        4 Ways to Listen
      </p>
      <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight leading-tight">
        Not just 8D.
        <br />
        <span className="text-[#b3b3b3] font-normal">Way beyond it.</span>
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-12">
        {MODES.map((mode) => (
          <GlowCard
            key={mode.tag}
            color={mode.color}
            className="rounded-lg overflow-hidden"
            style={{ background: "var(--surface)", boxShadow: "var(--shadow-card)" }}
          >
            <div
              data-feature-card
              style={{ visibility: "visible" }}
              className="relative p-6 hover:bg-white/[0.02] transition-colors duration-300"
            >
              {/* Left accent bar */}
              <div
                className="absolute left-0 top-4 bottom-4 w-[3px] rounded-full"
                style={{ background: mode.color, opacity: 0.6 }}
              />
              <div className="flex items-center gap-3 mb-4">
                <div style={{ color: mode.color }}>{mode.icon}</div>
                <span
                  className="text-[10px] font-bold uppercase tracking-[1.4px] px-2.5 py-1 rounded-full"
                  style={{ color: mode.color, background: `${mode.color}18` }}
                >
                  {mode.tag}
                </span>
              </div>
              <h3 className="text-base font-bold text-white mb-2">{mode.title}</h3>
              <p className="text-sm text-[#b3b3b3] leading-relaxed">{mode.desc}</p>
            </div>
          </GlowCard>
        ))}
      </div>
    </section>
  );
}
